import Base from '../../../Base/Base';
import Grid from '@mui/material/Grid';
import Modal from '@mui/material/Modal';
import { nanoid } from 'nanoid';
import { Button, IconButton } from '@upmc/ims.component-library';
import { Subtitle2 } from '../../../../components/UI/MDC/Typography/Typography';
import Icon from '../../../../components/UI/MDC/Icon/Icon';
import { debounce } from '../../../../utils/misc/debounce';
import { DataTable } from '../../../../components/UI/MDC/DataTable/DataTable';
import {
	fetchWorkQueueAdmins,
	fetchWorkQueueSystemList,
} from './WorkQueueAdmin_api';
import { useDispatch, useSelector } from 'react-redux';
import {
	init,
	setRows,
	sortByHeader,
} from '../../../../store/adminTable/actions';
import { init as wqInit } from '../../../../store/workQueue/actions';
import { HEADERS, HeaderDataCorrelation } from './_constants';
import { generateRowData } from './_utility';
import { UserContext } from 'context/User/UserContext';

import classes from './WorkQueueAdmin.module.scss';
import AdminTable from './AdminTable';
import RequestsTable from '../RequestsTable/RequestsTable';
import { WQAccordion } from '../../../../components/UI/WQAccordion/WQAccordion';

import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { ReactElement, useContext, useState, useEffect } from 'react';

/**
 * Wrapping component for Work Queue Page
 *
 * @returns {ReactElement} rendered component.
 */

const WorkQueueAdmin = () => {
	//#region REDUX STORE VARIABLES
	const rHeaders = useSelector(state => state.workQueueAdminTable.headers);
	const rRows = useSelector(state => state.workQueueAdminTable.rows);

	const currentSystem = useSelector(state => state.workQueue.currentSystem);

	//#endregion REDUX STORE VARIABLES

	const [openAdminModal, setOpenAdminModal] = useState(false);
	const [systemSelectDisabled, setSystemSelectDisabled] = useState(false);
	const [refreshDate, setRefreshDate] = useState(new Date().toLocaleString());
	const [loading, setLoading] = useState(false);

	const dispatch = useDispatch();
	/**
	 * @type {import('context/User/UserContext').ContextUser}
	 */
	const contextUser = useContext(UserContext);
	const user =
		contextUser.userInfo.impersonationProfile ??
		contextUser.userInfo.userProfile;

	const [totalSystems, setTotalSystems] = useState(undefined);
	const [loadingSystems, setLoadingSystems] = useState(true);

	const theme = createTheme({
		palette: {
			primary: {
				main: '#771b61',
			},
		},
	});

	const setSystem = system => {
		dispatch(
			wqInit({
				currentSystem: system,
			})
		);
	};

	const handleLoadingState = tableLoadingState => {
		setSystemSelectDisabled(tableLoadingState);
	};

	useEffect(() => {
		if (user?.ID) {
			setLoadingSystems(true);
			fetchWorkQueueSystemList({
				id: user.ID,
				admin: true,
			}).then(resp => {
				var data = resp?.data?.payload?.Results;

				if (data?.length > 0) {
					setTotalSystems(data);
					if (currentSystem === undefined) {
						setSystem(data[0]);
					}
				}

				setLoadingSystems(false);
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user]);

	// Fetch new work queue admins when the system changes
	useEffect(() => {
		if (currentSystem !== undefined) {
			GetWorkQueueAdmins();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentSystem]);

	// const switchToLegacy = () => {
	// 	window.top.location.href = `/${determineIfExternal(
	// 		`/workqueue/wqadmin.aspx`
	// 	)}`;
	// };
	/**
	 * Gets Work Queue Admins
	 *
	 * @returns void
	 */
	const GetWorkQueueAdmins = () => {
		setLoading(true);
		fetchWorkQueueAdmins({ WorkQueueID: currentSystem?.ID }).then(resp => {
			setLoading(false);
			if (resp?.data?.payload?.Results.length >= 0) {
				var rows = generateRowData(
					resp?.data?.payload?.Results,
					HeaderDataCorrelation
				);
				dispatch(setRows(rows));
				setRefreshDate(new Date().toLocaleString());
			}
		});
	};

	// Initial render configure Redux store for admins table
	useEffect(() => {
		dispatch(
			init({
				headers: HEADERS.map((header, index) => {
					return {
						Value: header,
						props: {
							onSortChange: sortOrder =>
								dispatch(sortByHeader(header, sortOrder)),
							sort: index === 0 ? 1 : null,
						},
					};
				}),
			})
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<Base
			className={classes['base--default']}
			loading={loadingSystems}
			title={'Work Queue Administration'}
		>
			<ThemeProvider theme={theme}>
				{totalSystems && (
					<Grid
						container
						direction='column'
						spacing={3}
						sx={{
							alignItems: 'center',
						}}
					>
						<Grid item>
							<FormControl>
								<InputLabel id='work-queue__input--wq-system'>
									Work Queue System
								</InputLabel>
								<Select
									disabled={systemSelectDisabled}
									className={
										classes['select--applications--default']
									}
									id='work-queue__select--wq-system'
									label='Work Queue System'
									value={currentSystem?.Name}
									onChange={e => {
										setSystem(
											...totalSystems.filter(
												x => x.Name === e.target.value
											)
										);
									}}
								>
									{Object.values(
										totalSystems?.map(x => x.Name)
									).map(e => (
										<MenuItem value={e}>{e}</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>
					</Grid>
				)}
				{loadingSystems === false && totalSystems?.length > 0 ? (
					<>
						<Modal open={openAdminModal}>
							<AdminTable
								{...{
									setOpenAdminModal,
									currentSystem,
								}}
							/>
						</Modal>

						<Grid
							container
							direction='column'
							spacing={3}
							sx={{
								alignItems: 'center',
							}}
						>
							<Grid item>
								{/* <Subtitle2
									className={
										classes['subtitle2--link--default']
									}
									onClick={switchToLegacy}
								>
									Switch to Legacy View
								</Subtitle2> */}
								<WQAccordion
									defaultExpanded={false}
									title={'Account Administration'}
									subTitle={'Add/Remove Account Admins'}
								>
									<DataTable
										{...{ loading }}
										rows={rRows}
										actions={[]}
										headers={rHeaders}
										label='Current Work Queue Admins'
									/>
									<Button
										sx={{ mt: '5px' }}
										id={
											'work-queue-admin__button--add-remove-admin'
										}
										aria-describedby={
											'work-queue-admin__button--add-remove-admin'
										}
										onClick={e => {
											setOpenAdminModal(true);
										}}
									>
										Add/Remove Work Queue Admin
									</Button>
									<IconButton
										className={
											classes['icon--refresh--default']
										}
										onClick={debounce(GetWorkQueueAdmins)}
										id={`refresh_icon${nanoid()}`}
									>
										<Icon icon='refresh' />
									</IconButton>
									<Subtitle2
										className={
											classes[
												'subTitle--refresh--default'
											]
										}
									>
										<b>Last Refreshed: </b>
										{refreshDate}
									</Subtitle2>
								</WQAccordion>
								<WQAccordion
									title={'Requests'}
									subTitle={'Work Queue Requests'}
								>
									<RequestsTable
										{...{
											currentSystem,
											handleLoadingState,
											GetWorkQueueAdmins,
										}}
										admin={true}
									/>
								</WQAccordion>
							</Grid>
						</Grid>
					</>
				) : (
					<Grid container sx={{ justifyContent: 'center' }}>
						<Grid item>
							You do not have privileges to any system to perform
							work queue administration tasks.
						</Grid>
					</Grid>
				)}
			</ThemeProvider>
		</Base>
	);
};

export default WorkQueueAdmin;
